import React, { useState } from "react";
import { useMatch, useNavigate } from "react-router-dom";

import { GET_USERS } from "graphql/query/user-gql";

import Users from "components/user";
import Icons from "components/icons";
import { useActiveMenuItem, useBreadCrumbs } from "components/use-hooks";
import { PageWrapDefault, Tables } from "components/layout";
import { Localize } from "components/service";
import { localizeByKey } from "components/service/localize";
import { gqlBuilderWhere } from "common/utils";
import { IFilter } from "components/user/helpers/user-types-helper";
import { PermissionButton } from "components/permissions";
import { usePageNavbarPermissions } from "components/permissions/hooks/use-page-navbar-permissions";
import UserConst from "components/user/users-const";
import RoleConst from "components/role/role-const";

import "./user.scss";

const basePath = Users.Const.basePath;
const tableHelper = Users.Helpers.TableMain;

const UsersPage: React.FC = () => {
  useActiveMenuItem(["users"], ["users"]);

  const breadcrumbs = [
    {
      label: localizeByKey("BREADCRUMBS.Label_Users"),
      path: `${basePath}`,
    },
  ];

  useBreadCrumbs(breadcrumbs);

  const navigate = useNavigate();

  const match = useMatch(`${basePath}/page/:pageNum`);

  const objOrderBy = [{ column: "ID", order: "DESC" }];

  const [searchText, setSearchText] = useState<string>("");
  const [filters, setFilters] = useState<IFilter | undefined>();
  const [filtersExtra, setFiltersExtra] = useState<IFilter | undefined>();
  const [filtersMore, setFiltersMore] = useState<IFilter | undefined>();
  const [resetSorting, setResetSorting] = useState<{ column: string; order: "ASC" | "DESC" }[]>();

  const usersFilters = JSON.parse(localStorage.getItem("usersFilters") || "{}");
  const usersFiltersExtra = JSON.parse(localStorage.getItem("usersFiltersExtra") || "{}");
  const usersFiltersMore = JSON.parse(localStorage.getItem("usersFiltersMore") || "{}");
  const usersSorting = JSON.parse(localStorage.getItem("usersSorting") || "[]");

  const objectFilters = filters || gqlBuilderWhere(usersFilters);
  const objectFilterExtra = filtersExtra || gqlBuilderWhere(usersFiltersExtra);
  const objectFiterMore = filtersMore || gqlBuilderWhere(usersFiltersMore);

  const pageNavbar = [
    {
      label: localizeByKey("NAVBAR_PAGES.Label_Users"),
      path: "users",
      permission: UserConst.permissions.VIEW,
    },
    {
      label: localizeByKey("NAVBAR_PAGES.Label_UserRoles"),
      path: "user-roles",
      permission: RoleConst.permissions.VIEW,
    },
  ];

  const { filteredNavbarItems } = usePageNavbarPermissions({ navbarItems: pageNavbar });

  return (
    <PageWrapDefault
      className="page-users"
      dataExist={true}
      title={<Localize>PAGES.Title_Users</Localize>}
      pageNavbar={filteredNavbarItems}
      staticPath=""
    >
      <Tables.Main
        model="users"
        query={GET_USERS}
        varSet={{
          perPage: 30,
          whereRoles: objectFilterExtra,
          whereTeams: objectFiterMore,
        }}
        routeUrl={`${basePath}`}
        currentPage={match ? parseInt(match.params.pageNum || "1") : 1}
        searchText={searchText}
        objectWhere={objectFilters}
        resetPaginationPage={
          typeof objectFilters === "object" ||
          typeof objectFilterExtra === "object" ||
          typeof objectFiterMore === "object"
        }
        objOrderBy={usersSorting || objOrderBy}
        sortingName="usersSorting"
        tableHelper={tableHelper}
        resetSorting={resetSorting}
      >
        <div className="table-action-bar">
          <div className="col">
            <Users.Filters.Table
              {...{
                setSearchText,
                filters,
                filtersExtra,
                filtersMore,
                setFilters,
                setFiltersExtra,
                setFiltersMore,
                usersFilters,
                usersFiltersExtra,
                usersFiltersMore,
                setResetSorting,
                objOrderBy,
              }}
            />
          </div>

          <div className="col">
            <PermissionButton
              permission={Users.Const.permissions.UPDATE}
              type="primary"
              icon={<Icons.Plus />}
              onClick={() => navigate(`${basePath}/create`)}
              disabledFallback={true}
            >
              <span className="ellipsis">
                <Localize>GLOBAL.Button_Text_CreateNew</Localize>
              </span>
            </PermissionButton>
          </div>
        </div>
      </Tables.Main>
    </PageWrapDefault>
  );
};

export default UsersPage;
